import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateEditRequest,
  EditRequestDto,
  PortalCarePlan,
  PortalInvoice,
  PortalProject,
  TimelineStep,
} from '@mys/contracts';
import { api } from './api.js';

/* ============================================================================
   The portal's queries.
   ----------------------------------------------------------------------------
   Everything here reads `/api/portal/*`, which is scoped to the logged-in
   client by the API. No screen passes a client id, because no screen has one
   and none should: the identity comes from the cookie, and an id in a URL is
   an id somebody will eventually change.

   Types come from `@mys/contracts` with `import type`, which is erased at
   compile time. Runtime values must come from `@mys/contracts/constants` or
   `/format` — the index pulls Zod into the bundle.
   ========================================================================== */

export function useProjects() {
  return useQuery({
    queryKey: ['portal-projects'],
    queryFn: ({ signal }) =>
      api.get<{ ok: true; projects: PortalProject[] }>('/api/portal/projects', signal),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['portal-project', id],
    queryFn: ({ signal }) =>
      api.get<{ ok: true; project: PortalProject }>(`/api/portal/projects/${id}`, signal),
    enabled: id !== '',
  });
}

export function useInvoices() {
  return useQuery({
    queryKey: ['portal-invoices'],
    queryFn: ({ signal }) =>
      api.get<{ ok: true; invoices: PortalInvoice[] }>('/api/portal/invoices', signal),
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: ['portal-invoice', id],
    queryFn: ({ signal }) =>
      api.get<{ ok: true; invoice: PortalInvoice }>(`/api/portal/invoices/${id}`, signal),
    enabled: id !== '',
  });
}

export function useCarePlans() {
  return useQuery({
    queryKey: ['portal-care-plans'],
    queryFn: ({ signal }) =>
      api.get<{ ok: true; carePlans: PortalCarePlan[] }>('/api/portal/care-plans', signal),
  });
}

export function useEditRequests() {
  return useQuery({
    queryKey: ['portal-edit-requests'],
    queryFn: ({ signal }) =>
      api.get<{ ok: true; editRequests: EditRequestDto[] }>('/api/portal/edit-requests', signal),
  });
}

export function useCreateEditRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEditRequest) =>
      api.post<{ ok: true; editRequest: EditRequestDto }>('/api/portal/edit-requests', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['portal-edit-requests'] }),
  });
}

/* --------------------------------------------------------------- DPDP ---- */

export function useUpdateProfile() {
  return useMutation({
    mutationFn: (changes: { ownerName?: string; email?: string }) =>
      api.patch<{ ok: true; message: string }>('/api/privacy/me', changes),
  });
}

export function useUpdateConsent() {
  return useMutation({
    mutationFn: (marketingConsent: boolean) =>
      api.post<{ ok: true; message: string }>('/api/privacy/consent', { marketingConsent }),
  });
}

export interface ErasureResult {
  ok: true;
  fullyDeleted: boolean;
  erased: string[];
  kept: string[];
  message: string;
}

export function useEraseMe() {
  return useMutation({
    mutationFn: (code: string) => api.del<ErasureResult>('/api/privacy/me', { code }),
  });
}

/* ------------------------------------------------------------- helpers ---- */

/** What to call a stage in a sentence, where the timeline shows it as a step. */
export const STATUS_LABELS: Record<string, string> = {
  brief: 'Planning',
  preview: 'Design ready',
  build: 'Ban raha hai',
  live: 'Live',
};

export const INVOICE_STATUS_LABELS: Record<string, string> = {
  due: 'Baaki hai',
  paid: 'Mil gaya',
  refunded: 'Wapas kiya',
};

export const INVOICE_TYPE_LABELS: Record<string, string> = {
  advance: 'Advance',
  final: 'Final',
  care: 'Care plan',
};

/**
 * The step a client is waiting on, or the last one if it is finished.
 *
 * Used for the one-line summary at the top of the home screen, so that someone
 * who opens the portal and looks at nothing else still learns the one thing
 * they came for.
 */
export function currentStep(timeline: TimelineStep[]): TimelineStep | undefined {
  return timeline.find((step) => step.current) ?? timeline.at(-1);
}

/**
 * Total still owed, in rupees, GST included. Only `due` — a refund is not a
 * debt.
 *
 * Re-exported rather than written here: this one was right and the office's
 * two copies were not, which is exactly why there is now one.
 */
export { outstandingInr } from '@mys/contracts/constants';
